import React, { useState, useEffect } from "react";
import {
    Popover,
    Box,
    Text,
    TextInput,
    ActionIcon,
    Loader,
    Autocomplete,
    Select,
} from "@mantine/core";
import { Button } from "@xplatform/design-system";
import { DatePickerInput } from "@mantine/dates";
import { IconX } from "@tabler/icons-react";
import dayjs from "dayjs";
import type {
    IProcessedResultsFilterViewModel,
    IUserSearchResultViewModel,
} from "@models/index";
import { xplatformCoreApi } from "@services/xplatformCoreApi";
import { useTranslation } from "@hooks/useTranslation";

import "./FilterPanel.scss";

interface FilterPanelProps {
    anchorEl: HTMLElement | null;
    open: boolean;
    onClose: () => void;
    onApplyFilter: (filter: IProcessedResultsFilterViewModel) => void;
    initialFilter: IProcessedResultsFilterViewModel;
}

const MAX_DATE_RANGE_DAYS = 30;
const LIMIT_OPTIONS = ["10", "25", "50", "100"];
const DEFAULT_LIMIT = "10";

const FilterPanel: React.FC<FilterPanelProps> = ({
    anchorEl,
    open,
    onClose,
    onApplyFilter,
    initialFilter,
}) => {
    const { t } = useTranslation();
    const [startDate, setStartDate] = useState<Date | null>(
        initialFilter.startDate ? dayjs(initialFilter.startDate).toDate() : null,
    );
    const [endDate, setEndDate] = useState<Date | null>(
        initialFilter.endDate ? dayjs(initialFilter.endDate).toDate() : null,
    );
    const [fileName, setFileName] = useState<string>(
        initialFilter.fileName || "",
    );
    const [processId, setProcessId] = useState<string>(
        initialFilter.processId || "",
    );
    const [selectedUser, setSelectedUser] =
        useState<IUserSearchResultViewModel | null>(null);
    const [userOptions, setUserOptions] = useState<IUserSearchResultViewModel[]>(
        [],
    );
    const [userSearchLoading, setUserSearchLoading] = useState(false);
    const [userInputValue, setUserInputValue] = useState("");
    const [dateRangeError, setDateRangeError] = useState<string>("");
    const [limit, setLimit] = useState<string>(
        initialFilter.limit ? String(initialFilter.limit) : DEFAULT_LIMIT,
    );

    useEffect(() => {
        if (open) {
            setStartDate(
                initialFilter.startDate
                    ? dayjs(initialFilter.startDate).toDate()
                    : null,
            );
            setEndDate(
                initialFilter.endDate ? dayjs(initialFilter.endDate).toDate() : null,
            );
            setFileName(initialFilter.fileName || "");
            setProcessId(initialFilter.processId || "");
            if (initialFilter.userId) {
                const displayValue = initialFilter.userEmail || initialFilter.userId;
                const option: IUserSearchResultViewModel = {
                    externalId: initialFilter.userId,
                    email: initialFilter.userEmail || initialFilter.userId,
                    firstName: null,
                    lastName: null,
                };
                setSelectedUser(option);
                setUserInputValue(displayValue);
            } else {
                setSelectedUser(null);
                setUserInputValue("");
            }
            setDateRangeError("");
            setLimit(initialFilter.limit ? String(initialFilter.limit) : DEFAULT_LIMIT);
        }
    }, [open, initialFilter]);

    useEffect(() => {
        if (userInputValue.length < 2) {
            setUserOptions([]);
            return;
        }

        const timer = setTimeout(async () => {
            setUserSearchLoading(true);
            try {
                const results = await xplatformCoreApi.searchUsers(userInputValue);
                setUserOptions(results);
            } catch {
                setUserOptions([]);
            } finally {
                setUserSearchLoading(false);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [userInputValue]);

    const validateDateRange = (start: Date | null, end: Date | null): boolean => {
        if (!start || !end) {
            setDateRangeError("");
            return true;
        }

        const diffDays = dayjs(end).diff(dayjs(start), "day");
        if (diffDays > MAX_DATE_RANGE_DAYS) {
            setDateRangeError(
                t("extraction.filterPanel.dateRange.errors.exceedsMaxRange", {
                    days: MAX_DATE_RANGE_DAYS,
                }),
            );
            return false;
        }

        if (diffDays < 0) {
            setDateRangeError(
                t("extraction.filterPanel.dateRange.errors.invalidRange"),
            );
            return false;
        }

        setDateRangeError("");
        return true;
    };

    const handleStartDateChange = (date: Date | null) => {
        setStartDate(date);
        validateDateRange(date, endDate);
    };

    const handleEndDateChange = (date: Date | null) => {
        setEndDate(date);
        validateDateRange(startDate, date);
    };

    const handleApply = () => {
        if (!validateDateRange(startDate, endDate)) {
            return;
        }

        const filter: IProcessedResultsFilterViewModel = {
            startDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : null,
            endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : null,
            fileName: fileName.trim() || null,
            userId: selectedUser?.externalId || null,
            userEmail: selectedUser?.email || userInputValue.trim() || null,
            processId: processId.trim() || null,
            limit: Number(limit),
        };

        onApplyFilter(filter);
        onClose();
    };

    const handleClear = () => {
        setStartDate(null);
        setEndDate(null);
        setFileName("");
        setProcessId("");
        if (initialFilter.userId) {
            const displayValue = initialFilter.userEmail || initialFilter.userId;
            const option: IUserSearchResultViewModel = {
                externalId: initialFilter.userId,
                email: initialFilter.userEmail || initialFilter.userId,
                firstName: null,
                lastName: null,
            };
            setSelectedUser(option);
            setUserInputValue(displayValue);
        } else {
            setSelectedUser(null);
            setUserInputValue("");
        }
        setDateRangeError("");
        setLimit(DEFAULT_LIMIT);
    };

    const getUserLabel = (option: IUserSearchResultViewModel) => {
        const parts = [option.firstName, option.lastName].filter(Boolean).join(" ");
        const email = option.email ?? "";
        return parts ? `${email} (${parts})` : email;
    };

    const autocompleteData = userOptions.map((o) => getUserLabel(o));

    return (
        <Popover
            opened={open}
            onClose={onClose}
            position="right-start"
            width={360}
            portalProps={{ target: "#extraction-portal-root" }}
        >
            <Popover.Target>
                <span
                    ref={(el) => {
                        if (el && anchorEl) {
                            el.style.position = "fixed";
                            const rect = anchorEl.getBoundingClientRect();
                            el.style.top = `${rect.top}px`;
                            el.style.left = `${rect.right}px`;
                            el.style.width = "0";
                            el.style.height = "0";
                        }
                    }}
                />
            </Popover.Target>

            <Popover.Dropdown className="filter-panel">
                <Box className="filter-header">
                    <Text size="lg" fw={600}>
                        {t("extraction.filterPanel.title")}
                    </Text>
                    <ActionIcon size="sm" variant="subtle" onClick={onClose}>
                        <IconX size={14} />
                    </ActionIcon>
                </Box>

                <Box className="filter-content">
                    <Box className="filter-section">
                        <Text size="sm" fw={600} className="filter-label">
                            {t("extraction.filterPanel.dateRange.label")}
                            <Text
                                size="xs"
                                c="dimmed"
                                component="span"
                                className="date-range-hint"
                                ml={4}
                            >
                                {t("extraction.filterPanel.dateRange.maxRangeHint", {
                                    days: MAX_DATE_RANGE_DAYS,
                                })}
                            </Text>
                        </Text>
                        <Box className="date-inputs">
                            <DatePickerInput
                                className="date-input"
                                size="xs"
                                label={t("extraction.filterPanel.dateRange.startDate")}
                                value={startDate}
                                onChange={handleStartDateChange}
                                valueFormat="DD/MM/YYYY"
                                placeholder="DD/MM/YYYY"
                                minDate={
                                    endDate
                                        ? dayjs(endDate).subtract(MAX_DATE_RANGE_DAYS, "day").toDate()
                                        : undefined
                                }
                                maxDate={
                                    endDate
                                        ? dayjs(endDate).isBefore(dayjs(), "day")
                                            ? endDate
                                            : new Date()
                                        : new Date()
                                }
                                popoverProps={{ withinPortal: true, zIndex: 9999 }}
                            />
                            <DatePickerInput
                                className="date-input"
                                size="xs"
                                label={t("extraction.filterPanel.dateRange.endDate")}
                                value={endDate}
                                onChange={handleEndDateChange}
                                valueFormat="DD/MM/YYYY"
                                placeholder="DD/MM/YYYY"
                                minDate={startDate || undefined}
                                maxDate={
                                    startDate
                                        ? dayjs(startDate)
                                            .add(MAX_DATE_RANGE_DAYS, "day")
                                            .isBefore(dayjs(), "day")
                                            ? dayjs(startDate)
                                                .add(MAX_DATE_RANGE_DAYS, "day")
                                                .toDate()
                                            : new Date()
                                        : new Date()
                                }
                                popoverProps={{ withinPortal: true, zIndex: 9999 }}
                            />
                        </Box>
                        {dateRangeError && (
                            <Text size="xs" c="red" className="error-text">
                                {dateRangeError}
                            </Text>
                        )}
                    </Box>

                    <Box className="filter-section">
                        <Text size="sm" fw={600} className="filter-label">
                            {t("extraction.filterPanel.fileName.label")}
                        </Text>
                        <TextInput
                            size="xs"
                            placeholder={t("extraction.filterPanel.fileName.placeholder")}
                            value={fileName}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setFileName(e.currentTarget.value)
                            }
                        />
                    </Box>

                    <Box className="filter-section">
                        <Text size="sm" fw={600} className="filter-label">
                            {t("extraction.filterPanel.batchId.label")}
                        </Text>
                        <TextInput
                            size="xs"
                            placeholder={t("extraction.filterPanel.batchId.placeholder")}
                            value={processId}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setProcessId(e.currentTarget.value)
                            }
                        />
                    </Box>

                    <Box className="filter-section">
                        <Text size="sm" fw={600} className="filter-label">
                            {t("extraction.filterPanel.itemLimit.label")}
                        </Text>
                        <Select
                            data={LIMIT_OPTIONS}
                            value={limit}
                            onChange={(val) => setLimit(val ?? DEFAULT_LIMIT)}
                            size="xs"
                            allowDeselect={false}
                            comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                        />
                    </Box>

                    <Box className="filter-section">
                        <Text size="sm" fw={600} className="filter-label">
                            {t("extraction.filterPanel.userEmail.label")}
                        </Text>
                        <Autocomplete
                            data={autocompleteData}
                            value={userInputValue}
                            filter={({ options }) => options}
                            comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                            onChange={(val: string) => {
                                setUserInputValue(val);
                                const match = userOptions.find((o) => getUserLabel(o) === val);
                                if (match) {
                                    setSelectedUser(match);
                                } else {
                                    setSelectedUser(
                                        val
                                            ? {
                                                externalId: null,
                                                email: val,
                                                firstName: null,
                                                lastName: null,
                                            }
                                            : null,
                                    );
                                }
                            }}
                            placeholder={t("extraction.filterPanel.userEmail.placeholder")}
                            size="xs"
                            rightSection={userSearchLoading ? <Loader size={14} /> : null}
                        />
                    </Box>
                </Box>

                <Box className="filter-actions">
                    <Button variant="outline" onClick={handleClear} size="xs">
                        {t("extraction.filterPanel.buttons.clear")}
                    </Button>
                    <Button onClick={handleApply} size="xs" disabled={!!dateRangeError}>
                        {t("extraction.filterPanel.buttons.apply")}
                    </Button>
                </Box>
            </Popover.Dropdown>
        </Popover>
    );
};

export default FilterPanel;
